import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Shield, 
  Coins, 
  Check,
  AlertCircle,
  Upload,
  Image,
  Palette,
  Crown
} from "lucide-react";

const COLOR_PRESETS = [
  // Tekli Renkler
  { id: "emerald", colors: ["#10b981"], name: "Zümrüt", scheme: "single" },
  { id: "blue", colors: ["#3b82f6"], name: "Mavi", scheme: "single" },
  { id: "red", colors: ["#ef4444"], name: "Kırmızı", scheme: "single" },
  { id: "purple", colors: ["#8b5cf6"], name: "Mor", scheme: "single" },
  { id: "orange", colors: ["#f97316"], name: "Turuncu", scheme: "single" },
  { id: "yellow", colors: ["#eab308"], name: "Sarı", scheme: "single" },
  { id: "pink", colors: ["#ec4899"], name: "Pembe", scheme: "single" },
  { id: "cyan", colors: ["#06b6d4"], name: "Cyan", scheme: "single" },
  
  // İkili Kombinasyonlar
  { id: "fenerbahce", colors: ["#FFED00", "#00205B"], name: "Sarı-Lacivert", scheme: "dual" },
  { id: "galatasaray", colors: ["#FDB913", "#8B0000"], name: "Sarı-Kırmızı", scheme: "dual" },
  { id: "besiktas", colors: ["#000000", "#FFFFFF"], name: "Siyah-Beyaz", scheme: "dual" },
  { id: "trabzon", colors: ["#6B1D2D", "#00A6D6"], name: "Bordo-Mavi", scheme: "dual" },
  { id: "blue-white", colors: ["#1e40af", "#ffffff"], name: "Mavi-Beyaz", scheme: "dual" },
  { id: "red-black", colors: ["#dc2626", "#171717"], name: "Kırmızı-Siyah", scheme: "dual" },
  { id: "green-gold", colors: ["#059669", "#fbbf24"], name: "Yeşil-Altın", scheme: "dual" },
  { id: "purple-gold", colors: ["#7c3aed", "#fcd34d"], name: "Mor-Altın", scheme: "dual" },
  
  // Üçlü Kombinasyonlar
  { id: "olympic", colors: ["#ef4444", "#ffffff", "#3b82f6"], name: "Kırmızı-Beyaz-Mavi", scheme: "triple" },
  { id: "reggae", colors: ["#16a34a", "#fbbf24", "#dc2626"], name: "Yeşil-Sarı-Kırmızı", scheme: "triple" },
  { id: "sunset", colors: ["#f97316", "#eab308", "#dc2626"], name: "Gün Batımı", scheme: "triple" },
  { id: "ocean", colors: ["#0ea5e9", "#06b6d4", "#14b8a6"], name: "Okyanus", scheme: "triple" },
];

const LOGO_CATEGORIES = {
  bird: "Kuşlar",
  feline: "Kedigiller",
  animal: "Diğer Hayvanlar",
  symbol: "Semboller"
};

const CreateClubPage = ({ user }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [logos, setLogos] = useState([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [logoTab, setLogoTab] = useState("default");
  const [colorTab, setColorTab] = useState("single");
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    logo: "eagle",
    logoType: "default",
    primaryColor: "#10b981",
    secondaryColor: null,
    tertiaryColor: null,
    colorScheme: "single",
    city: "",
    district: ""
  });

  const [customLogo, setCustomLogo] = useState(null);
  const [customLogoPreview, setCustomLogoPreview] = useState(null);

  const CREATION_COST = 50;
  const CUSTOM_LOGO_COST = 100;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [logosRes, balanceRes] = await Promise.all([
        axios.get(`${API}/clubs/logos`),
        axios.get(`${API}/credits/balance`)
      ]);
      
      setLogos(logosRes.data);
      setBalance(balanceRes.data.balance);
    } catch (error) {
      console.error("Veri yüklenemedi:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Max 2MB
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Dosya boyutu 2MB'dan küçük olmalı");
      return;
    }

    // Only images
    if (!file.type.startsWith('image/')) {
      toast.error("Sadece resim dosyası yükleyebilirsiniz");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setCustomLogo(reader.result);
      setCustomLogoPreview(reader.result);
      setFormData({ ...formData, logo: reader.result, logoType: "custom" });
    };
    reader.readAsDataURL(file);
  };

  const handleColorPresetSelect = (preset) => {
    setFormData({
      ...formData,
      primaryColor: preset.colors[0],
      secondaryColor: preset.colors[1] || null,
      tertiaryColor: preset.colors[2] || null,
      colorScheme: preset.scheme
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error("Kulüp adı gerekli");
      return;
    }
    
    const totalCost = CREATION_COST + (formData.logoType === "custom" ? CUSTOM_LOGO_COST : 0);
    
    if (balance < totalCost) {
      toast.error(`Bu işlem için ${totalCost} kredi gerekli`);
      return;
    }
    
    setCreating(true);
    try {
      const response = await axios.post(`${API}/clubs`, formData);
      
      if (response.data.success) {
        toast.success(response.data.message);
        navigate(`/clubs/${response.data.club.id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kulüp oluşturulamadı");
    } finally {
      setCreating(false);
    }
  };

  const getColorGradient = () => {
    if (formData.colorScheme === "triple" && formData.tertiaryColor) {
      return `linear-gradient(135deg, ${formData.primaryColor}, ${formData.secondaryColor}, ${formData.tertiaryColor})`;
    } else if (formData.colorScheme === "dual" && formData.secondaryColor) {
      return `linear-gradient(135deg, ${formData.primaryColor}, ${formData.secondaryColor})`;
    }
    return formData.primaryColor;
  };

  const selectedLogo = logos.find(l => l.id === formData.logo);
  const groupedLogos = logos.reduce((acc, logo) => {
    const cat = logo.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(logo);
    return acc;
  }, {});

  const totalCost = CREATION_COST + (formData.logoType === "custom" ? CUSTOM_LOGO_COST : 0);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="container mx-auto px-4 py-8">
          <Button 
            variant="ghost" 
            className="text-white hover:bg-white/20 mb-4"
            onClick={() => navigate("/clubs")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Kulüplere Dön
          </Button>
          
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Shield className="h-8 w-8" />
            Kulüp Kur
          </h1>
          <p className="text-emerald-100 mt-1">
            Kendi spor kulübünü oluştur ve yönet
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-3xl">
        {/* Cost Info */}
        <Card className={`mb-6 ${balance >= totalCost ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Coins className={`h-6 w-6 ${balance >= totalCost ? 'text-emerald-600' : 'text-red-600'}`} />
                <div>
                  <p className="font-medium">Toplam Maliyet</p>
                  <p className="text-sm text-gray-600">
                    Bakiyeniz: <span className="font-bold">{balance}</span> kredi
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold">{totalCost}</p>
                <p className="text-xs text-gray-500">
                  {CREATION_COST} (kulüp) {formData.logoType === "custom" && `+ ${CUSTOM_LOGO_COST} (özel logo)`}
                </p>
              </div>
            </div>
            
            {balance < totalCost && (
              <div className="mt-3 flex items-center gap-2 text-red-600">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">Yeterli krediniz yok</span>
                <Button 
                  size="sm" 
                  variant="outline"
                  className="ml-auto border-red-300 text-red-600 hover:bg-red-100"
                  onClick={() => navigate("/credits")}
                >
                  Kredi Al
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle>Temel Bilgiler</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Kulüp Adı *</Label>
                <Input
                  id="name"
                  placeholder="Örn: Kartal Spor Kulübü"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  maxLength={50}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Açıklama</Label>
                <Textarea
                  id="description"
                  placeholder="Kulübünüz hakkında kısa bir açıklama..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  maxLength={200}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">İl</Label>
                  <Input
                    id="city"
                    placeholder="İstanbul"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="district">İlçe</Label>
                  <Input
                    id="district"
                    placeholder="Kadıköy"
                    value={formData.district}
                    onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Logo Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Image className="h-5 w-5" />
                Logo Seçimi
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs value={logoTab} onValueChange={setLogoTab}>
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger value="default">
                    Ücretsiz Logolar
                  </TabsTrigger>
                  <TabsTrigger value="custom" className="flex items-center gap-1">
                    <Crown className="h-3 w-3" />
                    Özel Logo ({CUSTOM_LOGO_COST} Kredi)
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="default">
                  {Object.entries(groupedLogos).map(([category, categoryLogos]) => (
                    <div key={category} className="mb-4">
                      <h4 className="text-sm font-medium text-gray-500 mb-2">
                        {LOGO_CATEGORIES[category] || category}
                      </h4>
                      <div className="grid grid-cols-6 md:grid-cols-10 gap-2">
                        {categoryLogos.map((logo) => (
                          <button
                            key={logo.id}
                            type="button"
                            className={`p-2 rounded-lg border-2 transition-all hover:scale-105 ${
                              formData.logo === logo.id && formData.logoType === "default"
                                ? 'border-emerald-500 bg-emerald-50' 
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => setFormData({ 
                              ...formData, 
                              logo: logo.id, 
                              logoType: "default" 
                            })}
                            title={logo.name}
                          >
                            <span className="text-2xl block text-center">{logo.emoji}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </TabsContent>

                <TabsContent value="custom">
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center">
                    {customLogoPreview ? (
                      <div className="space-y-4">
                        <div className="w-24 h-24 mx-auto rounded-xl overflow-hidden">
                          <img src={customLogoPreview} alt="Özel logo" className="w-full h-full object-cover" />
                        </div>
                        <p className="text-sm text-gray-500">Logo seçildi</p>
                        <Button 
                          type="button"
                          variant="outline" 
                          onClick={() => {
                            setCustomLogo(null);
                            setCustomLogoPreview(null);
                            setFormData({ ...formData, logo: "eagle", logoType: "default" });
                          }}
                        >
                          Logoyu Kaldır
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <Upload className="h-12 w-12 mx-auto text-gray-400" />
                        <div>
                          <p className="font-medium">Özel logo yükle</p>
                          <p className="text-sm text-gray-500">PNG, JPG, max 2MB</p>
                        </div>
                        <Button 
                          type="button"
                          variant="outline"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          Dosya Seç
                        </Button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                          className="hidden"
                        />
                        <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                          +{CUSTOM_LOGO_COST} Kredi
                        </Badge>
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Color Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
                Renk Seçimi
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs value={colorTab} onValueChange={setColorTab}>
                <TabsList className="grid w-full grid-cols-3 mb-4">
                  <TabsTrigger value="single">Tek Renk</TabsTrigger>
                  <TabsTrigger value="dual">İkili</TabsTrigger>
                  <TabsTrigger value="triple">Üçlü</TabsTrigger>
                </TabsList>

                {["single", "dual", "triple"].map((scheme) => (
                  <TabsContent key={scheme} value={scheme}>
                    <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
                      {COLOR_PRESETS.filter(p => p.scheme === scheme).map((preset) => (
                        <button
                          key={preset.id}
                          type="button"
                          className={`group relative rounded-xl overflow-hidden transition-all hover:scale-105 ${
                            formData.primaryColor === preset.colors[0] && 
                            formData.colorScheme === preset.scheme
                              ? 'ring-2 ring-offset-2 ring-gray-400' 
                              : ''
                          }`}
                          onClick={() => handleColorPresetSelect(preset)}
                          title={preset.name}
                        >
                          <div 
                            className="h-12 w-full"
                            style={{ 
                              background: preset.scheme === "triple" 
                                ? `linear-gradient(135deg, ${preset.colors[0]} 33%, ${preset.colors[1]} 50%, ${preset.colors[2]} 67%)`
                                : preset.scheme === "dual"
                                  ? `linear-gradient(135deg, ${preset.colors[0]} 50%, ${preset.colors[1]} 50%)`
                                  : preset.colors[0]
                            }}
                          >
                            {formData.primaryColor === preset.colors[0] && 
                             formData.colorScheme === preset.scheme && (
                              <Check className="h-5 w-5 text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 drop-shadow-lg" />
                            )}
                          </div>
                          <p className="text-xs text-center py-1 bg-white truncate px-1">
                            {preset.name}
                          </p>
                        </button>
                      ))}
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>

          {/* Preview */}
          <Card className="overflow-hidden">
            <CardHeader className="pb-2">
              <CardTitle>Önizleme</CardTitle>
            </CardHeader>
            <CardContent>
              <div 
                className="p-6 rounded-xl"
                style={{ background: getColorGradient() }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-xl bg-white/90 backdrop-blur flex items-center justify-center overflow-hidden">
                    {formData.logoType === "custom" && customLogoPreview ? (
                      <img src={customLogoPreview} alt="Logo" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-5xl">{selectedLogo?.emoji || "🏟️"}</span>
                    )}
                  </div>
                  <div className="text-white">
                    <h3 className="font-bold text-2xl drop-shadow">
                      {formData.name || "Kulüp Adı"}
                    </h3>
                    <p className="text-white/80 drop-shadow">
                      {formData.city || "Şehir"}{formData.district ? `, ${formData.district}` : ""}
                    </p>
                    <Badge className="mt-2 bg-white/20 text-white border-white/30">
                      Bronz Seviye
                    </Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <Button
            type="submit"
            className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-lg"
            disabled={creating || balance < totalCost}
          >
            {creating ? (
              <span className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                Oluşturuluyor...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Kulübü Oluştur ({totalCost} Kredi)
              </span>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default CreateClubPage;
